import { render } from 'react-dom';

const RENDERED_SIZE_CONTAINER = 'rendered-size-container';

const GridUtils = {

    /**
     * Creates a new hidden container
     *
     * @param {Object} doc - document element
     * @returns {Element} element
     */
    getMainContainer(doc) {
        const container = doc.createElement('div');

        container.id = RENDERED_SIZE_CONTAINER;

        container.style.left = '-10000px';
        container.style.position = 'absolute';
        container.style.top = '-10000px';
        container.style.visibility = 'hidden';

        return container;
    },

    /**
     * Creates a new container on the given doc, with the desired width
     *
     * @param {Object} doc - document element
     * @param {string} type - Element type (.e.g div)
     * @param {number} width - width in pixels
     * @returns {Element} element
     */
    getNewContainer(doc, type, width) {
        const container = doc.createElement(type);

        container.style.width = typeof width === 'number' ? `${width}px` : width;

        return container;
    },

    /**
     * Renders the given element to in the given container
     *
     * @param {Element} container - container element
     * @param {Element} element - element to be rendered
     * @returns {Promise<Element>} promise that will return the rendered element reference
     */
    getRenderedElement(container, element) {
        let renderResult;

        return new Promise(success => {
            render(element, container, () => {
                renderResult = container.firstChild;
                success(renderResult);
            });
        });
    },

    /**
     * Renders the given element to get the real output height
     *
     * @param {Element} element - element to render
     * @param {number} containerWidth - container width where the element will be rendered in
     * @param {Object} [containerOptions] - custom options
     * @param {Object} [containerOptions.document] - document to render elements in. default: document
     * @param {string} [containerOptions.type] - element type (e.g. div) default: div
     * @returns {Promise<{ width: number, height: number }>} promise that will return the size of the element
     */
    getRenderedSize(element, containerWidth, containerOptions = {}) {
        let mainContainer = document.querySelector(`#${RENDERED_SIZE_CONTAINER}`);

        const { doc = document, type = 'div' } = containerOptions;

        if (!mainContainer) {
            mainContainer = this.getMainContainer(doc);

            doc.body.appendChild(mainContainer);
        }

        const renderContainer = this.getNewContainer(doc, type, containerWidth || doc.documentElement.clientWidth);

        mainContainer.appendChild(renderContainer);

        return new Promise(success => {
            this.getRenderedElement(renderContainer, element).then(renderedElement => {
                const size = {
                    height: renderedElement.offsetHeight,
                    width: renderedElement.offsetWidth
                };

                mainContainer.removeChild(renderContainer);

                success(size);
            });
        });
    },

    /**
     * Converts the pixel height to the best grid height unit size
     * This is compatible with react-grid-layout library
     *
     * @param {number} rowHeight - row height in pixels
     * @param {number} marginY - margin in pixels
     * @param {number} heightPixels - height in pixels (to be converted)
     * @returns {number} height grid units
     */
    calculatePixelsToHeightUnits(rowHeight, marginY, heightPixels) {
        let calcHeight = 0,
            calcRows = 0;

        // this is the formula from react-grid-layout to calculate the height in pixels
        // we use this to find a grid height unit size that will exceed the minimum height that we need
        while (heightPixels > calcHeight) {
            calcHeight = ++calcRows * rowHeight + (calcRows - 1) * marginY;
        }

        return calcRows;
    },

    /**
     * Will push the gridItems down or up depending on the startHeight threshold
     * by adjusting the y position
     *
     * @param {Array} gridItems - array of grid items. Grid items must have an y property, e.g. { y: 1 }
     * @param {number} startHeight - starting height (all below this threshold is affected)
     * @param {number} rows - rows to push down (can be negative for push up)
     * @returns {Array} array of grid items
     */
    alignAfterResize(gridItems, startHeight, rows) {
        const newGridItems = [...gridItems];

        for (let i = 0; i < gridItems.length; i++) {

            if (gridItems[i].y >= startHeight) {
                newGridItems[i] = { ...gridItems[i], y: gridItems[i].y + rows };
            }
        }

        return newGridItems;
    },

    /**
     * Will check if this grid item is expandable
     *
     * @param {Object} gridItem - Grid item. e.g. { y: 1, previousHeight: 1, height: 5 }
     * @param {Object|Element} element - element or react component that should be tested for output height
     * @param {number} containerWidth - container width in pixels (used as a parent to render the grid item at the given index)
     * @param {number} rowHeight - row height in pixels
     * @param {number} marginY - margin y in pixels
     * @returns {Promise<Array>} Promise that returns the adjusted array of grid items
     */
    isExpandable(gridItem, element, containerWidth, rowHeight, marginY) {
        return new Promise(success => {
            let isResizable = false;

            this.getRenderedSize(
                element,
                containerWidth
            ).then(size => {
                const originalHeightUnits = gridItem.originalHeight < gridItem.h ? gridItem.originalHeight : gridItem.h;
                const newHeightUnits = gridItem.originalHeight < gridItem.h ? gridItem.originalHeight : GridUtils.calculatePixelsToHeightUnits(
                    rowHeight,
                    marginY,
                    size.height
                );

                if (newHeightUnits > originalHeightUnits) {
                    isResizable = true;
                }

                success(isResizable);
            });
        });
    },

    /**
     * Will render the grid item at the given position in the given container, to determine the output height
     * which will be converted to grid height unit size. All grid items "below" this threshold will be adjusted
     * according to the expanding or collapsing grid item.
     *
     * @param {Array} gridItems - array of grid items. e.g. { y: 1, previousHeight: 1, height: 5 }
     * @param {number} itemIndex - grid item index that should be resized
     * @param {Object|Element} element - element or react component that should be tested for output height
     * @param {number} containerWidth - container width in pixels (used as a parent to render the grid item at the given index)
     * @param {number} rowHeight - row height in pixels
     * @param {number} marginY - margin y in pixels
     * @param {number} [verticalMargin] - (extra) margin (vertical) in pixels (e.g. for footer bar)
     * @returns {Promise<Array>} Promise that returns the adjusted array of grid items
     */
    resizeToDynamicHeight(gridItems, itemIndex, element, containerWidth, rowHeight, marginY, verticalMargin = 0) {
        return new Promise(success => {
            const gridItem = gridItems[itemIndex];
            let newGridItems = [...gridItems];

            this.getRenderedSize(
                element,
                containerWidth
            ).then(size => {
                const originalHeightUnits = gridItem.originalHeight < gridItem.h ? gridItem.originalHeight : gridItem.h;
                const newHeightUnits = gridItem.originalHeight < gridItem.h ? gridItem.originalHeight : GridUtils.calculatePixelsToHeightUnits(
                    rowHeight,
                    marginY,
                    size.height + verticalMargin
                );

                const newGridItem = newGridItems[itemIndex] = {
                    ...gridItem,
                    originalHeight: originalHeightUnits,
                    h: newHeightUnits
                };

                newGridItems = GridUtils.alignAfterResize(
                    newGridItems,
                    (newGridItem.y + newGridItem.originalHeight),
                    (newGridItem.h - newGridItem.originalHeight)
                );

                success(newGridItems);
            });
        });
    }
};

export default GridUtils;
